import { useTranslation } from "@/hooks/useTranslation";
import { useDeleteMultipleProxies, useDeleteSingleProxy } from "@/services";
import {
	ActionItem,
	ActionMenu,
	Button,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Icon,
	Typography,
	toast,
} from "@st-marketplace/ui";
import { type FC, useState } from "react";

export interface ProxiesDeleteActionProps {
	selectedIds: string[];
	onClear: () => void;
}

const ProxiesDeleteAction: React.FC<ProxiesDeleteActionProps> = ({
	selectedIds,
	onClear,
}) => {
	const { t } = useTranslation();
	const deleteProxiesMutation = useDeleteMultipleProxies();

	const [confirmDeleteDialog, setConfirmDeleteDialog] =
		useState<boolean>(false);

	const handleConfirm = () => {
		deleteProxiesMutation.mutate(
			{ body: selectedIds },
			{
				onSuccess: () => {
					setConfirmDeleteDialog(false);
					toast.success(
						t("proxy.deleteAction.successMessage", {
							value: selectedIds.length,
						}),
					);
					onClear();
				},
			},
		);
	};

	if (selectedIds.length === 0) return null;

	return (
		<>
			<div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-secondary-border bg-background-300 p-1">
				<Button icon variant="text" color="ghost" size="sm" onClick={onClear}>
					<Icon name="close-large-line" size={14} />
				</Button>

				<div className="flex items-center px-2 py-1.5">
					<Typography variant="body-md" className="font-semibold">
						{t("proxy.selectedProxies", { value: selectedIds.length })}
					</Typography>
				</div>

				<Button
					type="button"
					color="danger"
					size="sm"
					className="normal-case"
					startIcon={<Icon name="delete-bin-line" size="sm" />}
					onClick={() => setConfirmDeleteDialog(true)}
				>
					{t("proxy.buttonDeleteAction")}
				</Button>
			</div>

			<Dialog open={confirmDeleteDialog} onOpenChange={setConfirmDeleteDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("action.areYouSure")}</DialogTitle>
					</DialogHeader>
					<div className="px-4">
						{t("proxy.confirmDelete.text", { value: selectedIds.length })}
					</div>
					<DialogFooter
						buttons={{
							secondary: {
								label: t("proxy.confirmDelete.cancel"),
								props: {
									type: "button",
									onClick: () => setConfirmDeleteDialog(false),
								},
							},
							primary: {
								label: t("proxy.confirmDelete.buttonConfirm"),
								props: {
									type: "button",
									color: "danger",
									loading: deleteProxiesMutation.isPending,
									onClick: handleConfirm,
								},
							},
						}}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default ProxiesDeleteAction;

import ProxyFormEdit from "./Form/ProxyFormEdit";
import type { ProxyRow } from "./types";

export interface ProxiesSingleActionProps {
	selectedProxy: ProxyRow;
}

export const ProxiesSingleAction: FC<ProxiesSingleActionProps> = ({
	selectedProxy,
}) => {
	const { t } = useTranslation();
	const deleteProxyMutation = useDeleteSingleProxy();

	const [menuActionOpen, setMenuActionOpen] = useState<boolean>(false);
	const [editDialog, setEditDialog] = useState<boolean>(false);
	const [confirmDeleteDialog, setConfirmDeleteDialog] =
		useState<boolean>(false);

	const handleConfirm = () => {
		deleteProxyMutation.mutate(
			{ path: { proxy_id: selectedProxy.id } },
			{
				onSuccess: () => {
					setConfirmDeleteDialog(false);
					setMenuActionOpen(false);
				},
			},
		);
	};

	return (
		<>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation-only container; inner menu remains keyboard-accessible */}
			<div onClick={(event) => event.stopPropagation()}>
				<ActionMenu open={menuActionOpen} onOpenChange={setMenuActionOpen}>
					<ActionItem
						leadingIconName="edit-line"
						onClick={() => {
							setMenuActionOpen(false);
							setEditDialog(true);
						}}
					>
						{t("action.edit")}
					</ActionItem>
					<ActionItem
						leadingIconName="delete-bin-line"
						loading={deleteProxyMutation.isPending}
						onClick={() => {
							setMenuActionOpen(false);
							setConfirmDeleteDialog(true);
						}}
					>
						{t("action.delete")}
					</ActionItem>
				</ActionMenu>
			</div>

			<ProxyFormEdit
				selectedProxy={selectedProxy}
				open={editDialog}
				onClose={() => setEditDialog(false)}
			/>

			<Dialog open={confirmDeleteDialog} onOpenChange={setConfirmDeleteDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("action.areYouSure")}</DialogTitle>
					</DialogHeader>
					<div className="px-4">{t("proxy.action.deleteMessage")}</div>
					<DialogFooter
						buttons={{
							secondary: {
								label: t("common.button.no"),
								props: {
									type: "button",
									onClick: () => setConfirmDeleteDialog(false),
								},
							},
							primary: {
								label: t("action.button.delete"),
								props: {
									type: "button",
									color: "danger",
									loading: deleteProxyMutation.isPending,
									onClick: handleConfirm,
								},
							},
						}}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
};
